import React, { useEffect, useState } from "react";
import { Avatar, Button, Drawer, Layout, Popover, theme } from "antd";
import {
  LeftOutlined,
  LogoutOutlined,
  MoonOutlined,
  RightOutlined,
  SunOutlined,
  SyncOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useUser } from "../Context/UserContext";
import { AuthService } from "../Services/AuthService";
import newLogosmImg from "../images/AOIcon.svg";
import Sidemenus from "./Sidemenus";
import ChangePasswordModal from "./ChangePasswordModal";
import {
  SIDEBAR_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH,
  BREAKPOINT_SIDEBAR,
} from "./Layout/constants";

const { Sider } = Layout;

interface AppSidebarProps {
  collapsed: boolean;
  onCollapse: () => void;
  sidebarOpen: boolean;
  onSidebarClose: () => void;
  screenWidth: number;
}

const AppSidebar: React.FC<AppSidebarProps> = ({
  collapsed,
  onCollapse,
  sidebarOpen,
  onSidebarClose,
  screenWidth,
}) => {
  const { token } = theme.useToken();
  const { isSuperAdmin, isSubAdmin, isSupportAdmin, isSupportManager, isSupportUser, currentUser } = useUser();
  const navigate = useNavigate();

  /*
  const [themeMode, setThemeMode] = useState<"light" | "dark">(
    () => (localStorage.getItem("theme") as "light" | "dark") || "light"
  );
  */
  const [openPopover, setOpenPopover] = useState(false);
  const [openChangePassword, setOpenChangePassword] = useState(false);
  const [imagePath, setImagePath] = useState<any>(currentUser?.thumbUrl.get());

  const userName: any = currentUser?.name.get();
  const userEmail: any = currentUser?.email.get();
  const userId: any = currentUser?.id.get();

  useEffect(() => {
    setImagePath(currentUser?.thumbUrl.get());
  }, [currentUser]);

  /*
  useEffect(() => {
    const handler = (e: Event) => {
      setThemeMode((e as CustomEvent).detail as "light" | "dark");
    };
    window.addEventListener("themeChange", handler);
    return () => window.removeEventListener("themeChange", handler);
  }, []);

  const toggleTheme = () => {
    const next = themeMode === "light" ? "dark" : "light";
    setThemeMode(next);
    localStorage.setItem("theme", next);
    window.dispatchEvent(new CustomEvent("themeChange", { detail: next }));
  };
  */

  const handleLogout = () => {
    AuthService.logout().then(() => navigate("/login"));
    setOpenPopover(false);
  };

  let roleLabel = "Sub Admin";
  if (isSuperAdmin()) {
    roleLabel = "Super Admin";
  } else if (isSupportAdmin && isSupportAdmin()) {
    roleLabel = "Support Admin";
  } else if (isSupportManager && isSupportManager()) {
    roleLabel = "Support Manager";
  } else if (isSupportUser && isSupportUser()) {
    roleLabel = "Support User";
  } else if (isSubAdmin && isSubAdmin()) {
    roleLabel = "Sub Admin";
  } else {
    const userRoles: string[] = (currentUser?.roles?.get({ noproxy: true }) as any) || [];
    if (userRoles.includes("SUPPORTADMIN") || userRoles.includes("SupportAdmin")) {
      roleLabel = "Support Admin";
    } else if (userRoles.includes("SUPPORTMANAGER") || userRoles.includes("SupportManager")) {
      roleLabel = "Support Manager";
    } else if (userRoles.includes("SUPPORTUSER") || userRoles.includes("SupportUser")) {
      roleLabel = "Support User";
    }
  }

  const isSuper = isSuperAdmin();
  const isSupportRole = roleLabel.startsWith("Support");
  const roleLabelColor = isSuper ? token.colorPrimary : isSupportRole ? token.colorInfo : token.colorWarning;
  const roleLabelBg = isSuper ? token.colorPrimaryBg : isSupportRole ? token.colorInfoBg : token.colorWarningBg;

  const drawerWidth =
    screenWidth < 600 ? 160 : screenWidth < 768 ? 180 : screenWidth < 1000 ? 200 : 220;

  // ── Logo / branding block ────────────────────────────────────────────────
  const LogoBlock = ({ showCollapse }: { showCollapse: boolean }) => (
    <div
      style={{
        padding: collapsed && showCollapse ? "6px 0" : "8px 10px",
        display: "flex",
        flexDirection: collapsed && showCollapse ? "column" : "row",
        alignItems: "center",
        justifyContent: collapsed && showCollapse ? "center" : "space-between",
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
        height: collapsed && showCollapse ? 64 : 48,
        gap: collapsed && showCollapse ? 4 : 0,
        flexShrink: 0,
      }}
    >
      {collapsed && showCollapse ? (
        <img
          style={{ height: 24, width: "auto", cursor: "pointer", transition: "all 0.3s" }}
          src={newLogosmImg}
          alt="AO Console"
          onClick={onCollapse}
        />
      ) : (
        <div style={{ display: "flex", alignItems: "center" }}>
          <img
            style={{ height: 28, width: "auto", cursor: "pointer", transition: "all 0.3s" }}
            src={newLogosmImg}
            alt="AO Console"
            onClick={showCollapse ? onCollapse : undefined}
          />
          <div style={{ display: "flex", flexDirection: "column", marginLeft: 6 }}>
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: token.colorText,
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
              }}
            >
              AO Console
            </span>
            <span
              style={{
                fontSize: 9,
                fontWeight: 600,
                color: roleLabelColor,
                backgroundColor: roleLabelBg,
                padding: "2px 6px",
                borderRadius: 4,
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                width: "fit-content",
                marginTop: 2,
                lineHeight: 1,
              }}
            >
              {roleLabel}
            </span>
          </div>
        </div>
      )}

      {showCollapse && (
        <Button
          type="text"
          icon={
            collapsed ? (
              <RightOutlined style={{ fontSize: 10, color: token.colorTextSecondary }} />
            ) : (
              <LeftOutlined style={{ fontSize: 11, color: token.colorTextSecondary }} />
            )
          }
          onClick={onCollapse}
          style={{
            width: collapsed ? 20 : 24,
            height: collapsed ? 20 : 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
            borderRadius: 4,
          }}
        />
      )}
    </div>
  );

  // ── User popover content ─────────────────────────────────────────────────
  const UserPopoverContent = () => (
    <div className="profile-popover-content">
      <div className="profile-popover-header">
        {imagePath ? (
          <Avatar src={imagePath} className="popover-avatar" />
        ) : (
          <Avatar icon={<UserOutlined />} className="popover-avatar" />
        )}
        <div className="popover-user-info">
          <div className="popover-username">{userName}</div>
          <div className="popover-email">{userEmail}</div>
        </div>
      </div>

      <div className="profile-popover-divider" />

      <div className="profile-popover-menu">
        <button
          className="popover-menu-item"
          onClick={() => {
            navigate(`/adminuser/profile/${userId}`);
            setOpenPopover(false);
          }}
        >
          <UserOutlined className="popover-menu-icon" />
          <span>Profile</span>
        </button>

        <button
          className="popover-menu-item"
          onClick={() => {
            setOpenChangePassword(true);
            setOpenPopover(false);
          }}
        >
          <SyncOutlined className="popover-menu-icon" />
          <span>Change password</span>
        </button>

        <button className="popover-menu-item logout" onClick={handleLogout}>
          <LogoutOutlined rotate={-90} className="popover-menu-icon" />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );

  // ── Footer (theme toggle + user card) ───────────────────────────────────
  const SidebarFooter = ({ isCollapsed }: { isCollapsed: boolean }) => (
    <div
      style={{
        padding: "8px 10px",
        borderTop: `1px solid ${token.colorBorderSecondary}`,
        background: token.colorBgContainer,
        display: "flex",
        flexDirection: "column",
        gap: 6,
        flexShrink: 0,
      }}
    >
      {/* Theme toggle row (commented out - Light theme only) */}
      {/*
      <div
        style={{
          display: "flex",
          justifyContent: isCollapsed ? "center" : "space-between",
          alignItems: "center",
          padding: "4px 6px",
          borderRadius: token.borderRadius,
          background: token.colorFillTertiary,
          transition: "all 0.2s ease",
        }}
      >
        {!isCollapsed && (
          <span style={{ fontSize: 11, fontWeight: 500, color: token.colorTextSecondary }}>
            {themeMode === "dark" ? "Dark Mode" : "Light Mode"}
          </span>
        )}
        <Button
          type="text"
          size="small"
          onClick={toggleTheme}
          icon={
            themeMode === "dark" ? (
              <SunOutlined style={{ color: token.colorWarning }} />
            ) : (
              <MoonOutlined style={{ color: token.colorTextSecondary }} />
            )
          }
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 24,
            height: 24,
            borderRadius: 4,
          }}
        />
      </div>
      */}

      {/* User card */}
      <Popover
        placement={isCollapsed ? "rightBottom" : "topRight"}
        content={<UserPopoverContent />}
        trigger="click"
        open={openPopover}
        onOpenChange={setOpenPopover}
        overlayClassName="profile-popover"
      >
        <div
          className={`user-profile-card ${isCollapsed ? "collapsed" : ""}`}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: isCollapsed ? "center" : "space-between",
            padding: "4px 6px",
            borderRadius: token.borderRadius,
            cursor: "pointer",
            transition: "all 0.2s ease",
            background: openPopover ? token.colorFillTertiary : "transparent",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Avatar
              icon={<UserOutlined />}
              src={imagePath}
              style={{
                width: 28,
                height: 28,
                backgroundColor: token.colorPrimaryBg,
                color: token.colorPrimary,
                border: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            />
            {!isCollapsed && (
              <div style={{ display: "flex", flexDirection: "column", maxWidth: 130 }}>
                <span
                  style={{
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: token.colorText,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: 1.2,
                  }}
                >
                  {userName}
                </span>
                <span
                  style={{
                    fontSize: 10,
                    color: token.colorTextSecondary,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: 1.2,
                    marginTop: 1,
                  }}
                >
                  {userEmail}
                </span>
              </div>
            )}
          </div>
          {!isCollapsed && (
            <RightOutlined style={{ fontSize: 10, color: token.colorTextTertiary }} />
          )}
        </div>
      </Popover>
    </div>
  );

  return (
    <>
      {/* Desktop Sider */}
      {screenWidth >= BREAKPOINT_SIDEBAR && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={SIDEBAR_WIDTH}
          collapsedWidth={SIDEBAR_COLLAPSED_WIDTH}
          style={{
            backgroundColor: token.colorBgContainer,
            height: "100vh",
            position: "fixed",
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            borderRight: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <LogoBlock showCollapse />
            <div style={{ flex: 1, overflowY: "auto", padding: "2px 0" }}>
              <Sidemenus isCollapse={collapsed} onsetSlide={() => {}} styles={undefined} isSlide={false} />
            </div>
            <SidebarFooter isCollapsed={collapsed} />
          </div>
        </Sider>
      )}

      {/* Mobile Drawer */}
      <Drawer
        className="sidebar-drawer"
        title={
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <LogoBlock showCollapse={false} />
            <Button
              type="text"
              icon={<LeftOutlined style={{ fontSize: 11, color: token.colorTextSecondary }} />}
              onClick={onSidebarClose}
              style={{ width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", padding: 0, borderRadius: 4 }}
            />
          </div>
        }
        placement="left"
        onClose={onSidebarClose}
        open={sidebarOpen}
        width={drawerWidth}
        closable={false}
      >
        <Sidemenus isCollapse={false} onsetSlide={onSidebarClose} styles={undefined} isSlide={false} />
      </Drawer>

      {/* Change Password Modal */}
      <ChangePasswordModal
        open={openChangePassword}
        onClose={() => setOpenChangePassword(false)}
        userId={userId}
      />
    </>
  );
};

export default AppSidebar;
